import React, { useState, useEffect, useRef } from 'react';
import { apiRequest } from '../../utils/api';
import MenuPickerBottomSheet from './menuPickerBottomSheet';

/**
 * 품절 일괄 적용 모달 (판매시간 일괄 적용과 동일 패턴).
 * - 메뉴 편집에서 품절/자동해제 수정 + 저장 시 호출됨.
 * - 1단계: "다른 메뉴들도 일괄 적용하시겠습니까?" 확인 팝업
 * - 2단계: MenuPickerBottomSheet → 메뉴 선택 후 적용
 *
 * soldOut = { is_soldout, auto_resume_soldout } — 방금 저장한 품절 값
 */
const SoldOutBulkApply = ({ isOpen, onClose, onApplied, storeId, soldOut }) => {
  const [step, setStep] = useState('confirm'); // 'confirm' | 'picker' | 'closing'
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimerRef = useRef(null);
  const appliedRef = useRef(false);

  const showToast = (message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ show: true, message, type });
    toastTimerRef.current = setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 1800);
  };

  useEffect(() => {
    if (isOpen) {
      setStep('confirm');
      setSelectedKeys([]);
      appliedRef.current = false;
    }
  }, [isOpen]);

  const handleSelectMenu = (menuKey) => {
    setSelectedKeys(prev => [...prev.filter(k => k !== menuKey), menuKey]);
  };

  const handleDeselectMenu = (menuKey) => {
    setSelectedKeys(prev => prev.filter(k => k !== menuKey));
  };

  const handleConfirm = async () => {
    if (!storeId || selectedKeys.length === 0) return;
    try {
      const r = await apiRequest(`/menus/${storeId}/bulk-soldout`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          menu_keys: selectedKeys,
          is_soldout: soldOut?.is_soldout || false,
          auto_resume_soldout: soldOut?.auto_resume_soldout || false,
        }),
      });
      if (r.success) {
        appliedRef.current = true;
      } else {
        showToast(r.message || '적용 실패', 'error');
        return;
      }
    } catch (e) {
      showToast('적용 요청 실패: ' + e.message, 'error');
      return;
    }
    handlePickerClose();
  };

  const closeAll = () => {
    if (appliedRef.current && onApplied) onApplied();
    appliedRef.current = false;
    onClose();
  };

  const handlePickerClose = () => {
    setStep('closing');
    setTimeout(closeAll, 220);
  };

  if (!isOpen) return null;

  const summary = soldOut?.is_soldout
    ? `품절 처리${soldOut?.auto_resume_soldout ? ' (다음 영업일 자동 재판매)' : ''}`
    : '품절 해제';

  return (
    <>
      {step === 'confirm' && (
        <div className="soba-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeAll(); }}>
          <div className="soba-dialog">
            <div className="soba-title">다른 메뉴들도 일괄 적용하시겠습니까?</div>
            <div className="soba-summary">{summary}</div>
            <div className="soba-actions">
              <button className="soba-btn cancel" onClick={closeAll}>아니요</button>
              <button className="soba-btn confirm" onClick={() => setStep('picker')}>네</button>
            </div>
          </div>
        </div>
      )}

      <MenuPickerBottomSheet
        isOpen={step === 'picker'}
        onClose={handlePickerClose}
        storeId={storeId}
        mode="grouped"
        registeredKeys={selectedKeys}
        onSelect={handleSelectMenu}
        onDeselect={handleDeselectMenu}
        showFooter={true}
        onConfirm={handleConfirm}
        title="품절 일괄 적용할 메뉴 선택"
      />

      {toast.show && <div className={`soba-toast ${toast.type}`}>{toast.message}</div>}

      <style jsx>{`
        .soba-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.45);
          z-index: 10060; display: flex; align-items: center; justify-content: center;
          padding: 24px;
        }
        .soba-dialog {
          background: #fff; border-radius: 16px; padding: 28px;
          width: 100%; max-width: 340px; text-align: center;
          box-shadow: 0 10px 40px rgba(0,0,0,0.18);
        }
        .soba-title { font-size: 16px; font-weight: 700; color: #212529; line-height: 1.4; }
        .soba-summary {
          margin-top: 18px; font-size: 14px; font-weight: 600; color: #1971c2;
          background: #e7f5ff; border-radius: 8px; padding: 8px 12px;
        }
        .soba-actions { display: flex; gap: 10px; margin-top: 20px; }
        .soba-btn {
          flex: 1; height: 50px; border-radius: 10px; font-size: 16px; font-weight: 600;
          border: none; cursor: pointer;
        }
        .soba-btn.cancel { background: #f1f3f5; color: #495057; }
        .soba-btn.confirm { background: #FDCD27; color: #FFFFFF; }
        .soba-btn.cancel:active, .soba-btn.confirm:active { transform: scale(0.98); }
        .soba-toast {
          position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%);
          padding: 10px 18px; border-radius: 22px; font-size: 13px;
          background: #212529; color: #fff; z-index: 10070;
        }
        .soba-toast.error { background: #c92a2a; }
      `}</style>
    </>
  );
};

export default SoldOutBulkApply;
